import { Injectable, OnDestroy, Inject, forwardRef } from "@angular/core";
import { BehaviorSubject, Observable, Subject, Subscription } from "rxjs";
import { User } from "./models/user.model";
import { Tenant, TenantUser } from "./models/tenant.model";
import { UserPermissions } from "./models/role.model";
import { takeUntil, finalize } from "rxjs/operators";
import { RoleService } from "./role.service";

@Injectable({ providedIn: "root" })
export class CurrentService implements OnDestroy {
  private subscriptions: Subscription[] = [];

  public currentUser: BehaviorSubject<User> = new BehaviorSubject<User>(null);
  public currentTenant: BehaviorSubject<Tenant> = new BehaviorSubject<Tenant>(null);
  public currentTenantUser: BehaviorSubject<TenantUser> = new BehaviorSubject<TenantUser>(null);
  public currentPermissions: BehaviorSubject<UserPermissions | null> =
    new BehaviorSubject<UserPermissions | null>(null);

  private tenantChangeSignal = new Subject<void>();

  // Signal pour indiquer qu'une déconnexion est en cours
  private _isLoggingOut = new BehaviorSubject<boolean>(false);
  public isLoggingOut$ = this._isLoggingOut.asObservable();

  public isLoading: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  // Injection du RoleService via forwardRef pour éviter les dépendances circulaires
  private roleService: RoleService | null = null;

  constructor() {
    this.initFromLocalStorage();

    this.subscriptions.push(
      this.currentUser.subscribe((user) => {
        if (user) {
          localStorage.setItem("currentUser", JSON.stringify(user));
        } else {
          localStorage.removeItem("currentUser");
        }
      })
    );

    this.subscriptions.push(
      this.currentTenant.subscribe((tenant) => {
        if (tenant) {
          localStorage.setItem("currentTenant", JSON.stringify(tenant));
        } else {
          localStorage.removeItem("currentTenant");
        }
      })
    );

    this.subscriptions.push(
      this.currentTenantUser.subscribe((tenantUser) => {
        if (tenantUser) {
          localStorage.setItem("currentTenantUser", JSON.stringify(tenantUser));
        } else {
          localStorage.removeItem("currentTenantUser");
        }
      })
    );

    this.subscriptions.push(
      this.currentPermissions.subscribe((permissions) => {
        if (permissions) {
          localStorage.setItem("currentPermissions", JSON.stringify(permissions));
        } else {
          localStorage.removeItem("currentPermissions");
        }
      })
    );
  }

  /**
   * Injecte le RoleService (appelé depuis AppModule ou après l'initialisation)
   */
  setRoleService(roleService: RoleService): void {
    this.roleService = roleService;

    // S'abonner aux changements de permissions du RoleService
    this.subscriptions.push(
      roleService.userRole$.subscribe((userPermissions) => {
        this.currentPermissions.next(userPermissions);
      })
    );
  }

  private initFromLocalStorage(): void {
    const storedUser = localStorage.getItem("currentUser");
    const storedTenant = localStorage.getItem("currentTenant");
    const storedTenantUser = localStorage.getItem("currentTenantUser");
    const storedPermissions = localStorage.getItem("currentPermissions");

    if (storedUser) {
      try {
        this.currentUser.next(JSON.parse(storedUser));
      } catch (e) {
        console.error("Error parsing stored user", e);
        localStorage.removeItem("currentUser");
      }
    }

    if (storedTenant) {
      try {
        this.currentTenant.next(JSON.parse(storedTenant));
      } catch (e) {
        console.error("Error parsing stored tenant", e);
        localStorage.removeItem("currentTenant");
      }
    }

    if (storedTenantUser) {
      try {
        this.currentTenantUser.next(JSON.parse(storedTenantUser));
      } catch (e) {
        console.error("Error parsing stored tenant user", e);
        localStorage.removeItem("currentTenantUser");
      }
    }

    if (storedPermissions) {
      try {
        this.currentPermissions.next(JSON.parse(storedPermissions));
      } catch (e) {
        console.error("Error parsing stored permissions", e);
        localStorage.removeItem("currentPermissions");
      }
    }
  }

  setTenant(tenant: Tenant): void {
    this.isLoading.next(true);
    this.tenantChangeSignal.next();
    this.currentTenant.next(tenant);

    // Recharger les permissions lors du changement de tenant
    if (this.roleService) {
      this.roleService.refreshPermissions().subscribe({
        next: () => console.log("✅ Permissions rechargées après changement de tenant"),
        error: (err) => console.error("Erreur rechargement permissions:", err),
        complete: () => setTimeout(() => this.isLoading.next(false), 100),
      });
    } else {
      setTimeout(() => this.isLoading.next(false), 100);
    }
  }

  setUser(user: User): void {
    this.currentUser.next(user);
  }

  setTenantUser(tenantUser: TenantUser): void {
    this.currentTenantUser.next(tenantUser);
  }

  /**
   * Vérifie si une déconnexion est en cours
   */
  get isLoggingOut(): boolean {
    return this._isLoggingOut.value;
  }

  /**
   * Signale le début d'une déconnexion (bloque les appels API)
   */
  startLogout(): void {
    this._isLoggingOut.next(true);
    this.tenantChangeSignal.next(); // Stoppe les observables en cours
  }

  /**
   * Réinitialise toutes les données utilisateur
   */
  resetAll(): void {
    // Signaler que la déconnexion est en cours AVANT de reset
    this._isLoggingOut.next(true);
    this.tenantChangeSignal.next();

    this.currentUser.next(null);
    this.currentTenant.next(null);
    this.currentTenantUser.next(null);
    this.currentPermissions.next(null);
    localStorage.removeItem("currentUser");
    localStorage.removeItem("currentTenant");
    localStorage.removeItem("currentTenantUser");
    localStorage.removeItem("currentPermissions");

    // Effacer les permissions du RoleService
    if (this.roleService) {
      this.roleService.clearPermissions();
    }

    // Reset le flag après un court délai pour permettre la navigation
    setTimeout(() => this._isLoggingOut.next(false), 100);
  }

  // ==================== HELPERS PERMISSIONS ====================

  /**
   * Vérifie si l'utilisateur a une permission (raccourci)
   */
  hasPermission(permissionCode: string): boolean {
    return this.roleService?.hasPermission(permissionCode) ?? false;
  }

  /**
   * Vérifie si l'utilisateur a au moins une des permissions
   */
  hasAnyPermission(permissionCodes: string[]): boolean {
    return this.roleService?.hasAnyPermission(permissionCodes) ?? false;
  }

  /**
   * Vérifie si l'utilisateur a toutes les permissions
   */
  hasAllPermissions(permissionCodes: string[]): boolean {
    return this.roleService?.hasAllPermissions(permissionCodes) ?? false;
  }

  /**
   * Récupère le nom du rôle courant
   */
  getCurrentRoleName(): string | null {
    return this.currentPermissions.value?.roleName || null;
  }

  /**
   * Charge les permissions de l'utilisateur (à appeler après le login)
   */
  loadPermissions(): Observable<UserPermissions> | null {
    return this.roleService?.loadUserPermissions() ?? null;
  }

  takeUntilTenantChange<T>(observable: Observable<T>): Observable<T> {
    return observable.pipe(
      takeUntil(this.tenantChangeSignal),
      finalize(() => console.info("Observable terminé après changement de tenant"))
    );
  }

  getTenantChangeSignal(): Observable<void> {
    return this.tenantChangeSignal.asObservable();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
    this.subscriptions = [];
  }
}
